import { Box, Button, Container, Divider, Link, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { LoginProps } from '../../interfaces/ILogin';

import LoginForm from '../../components/login/login-form';
import Logo from '../../components/login/logo';
import SocialAuth from '../../components/login/social';

import { cleanMeetingFromLocalStorage, handleDeleteAllCookies } from '../../helpers/localStorage';
import { ContentStyle } from '../../style/content-style';
import { HeadingStyle } from '../../style/heading-style';
import { fadeInUp } from './auth-styles';
import { AppDispatch } from '@store/types';
import { fetchInstanceData } from '../../components/authentication/initialization.thunks';
import { offersCredentials } from '../../components/login/sso.helpers';
import { selectAuth } from '@store/selectors';

const Login: React.FC<LoginProps> = (props: LoginProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { isTeamsApp } = useSelector(selectAuth);
  // SSO is the only way in unless this visit explicitly asked for the
  // credentials form. Read once: it must not react to later navigation.
  const [credentialsOffered] = React.useState(() => offersCredentials(window.location.search));
  // Asking for the form is the whole point of the parameter, so it starts
  // open; the toggle below is then a way to fold it back.
  const [showInformation, setShowInformation] = React.useState(credentialsOffered);

  useEffect(() => {
    if (isTeamsApp) {
      window.location.href = '/config';
      return;
    }

    cleanMeetingFromLocalStorage();
    handleDeleteAllCookies();
    dispatch(fetchInstanceData());
  }, [isTeamsApp]);

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Container>
        <ContentStyle>
          <HeadingStyle component={motion.div} {...fadeInUp}>
            <Logo />
            <Typography sx={{ color: 'text.secondary', mb: 5 }}>
              {t('GLOBAL_AUTH_LOGIN_TO_ACCOUNT', {
                defaultValue: 'Login to your account',
              })}
            </Typography>
          </HeadingStyle>
          <Box
            component={motion.div}
            {...fadeInUp}
            sx={{
              backgroundColor: 'background.default',
            }}
          >
            <SocialAuth direction="row" {...props} />
          </Box>

          {credentialsOffered && (
            <>
              <Divider sx={{ my: 3 }} component={motion.div} {...fadeInUp}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('GLOBAL_AUTH_OR', { defaultValue: 'OR' })}
                </Typography>
              </Divider>
              <Box component={motion.div} {...fadeInUp}>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={() => setShowInformation(!showInformation)}
                  startIcon={
                    <img
                      src="/assets/images/secondbrain_grey.svg"
                      alt="secondbrain"
                      width="20"
                      height="20"
                    />
                  }
                  sx={{
                    border: '2px solid #ccc',
                    borderRadius: '5px',
                    padding: '0.5675rem',
                    flex: 1,
                    color: 'text.secondary',
                    ':hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      border: '2px solid #ccc',
                      padding: '0.5675rem',
                      flex: 1,
                    },
                  }}
                >
                  {t('GLOBAL_AUTH_USE_SBACCOUNT', { defaultValue: 'Use SecondBrain account' })}
                </Button>
              </Box>
              {showInformation && (
                <Box component={motion.div} {...fadeInUp} sx={{ mt: 4 }}>
                  <LoginForm />
                </Box>
              )}
              <Typography
                component={motion.p}
                {...fadeInUp}
                variant="body2"
                align="center"
                sx={{ mt: 4 }}
              >
                {`${t('GLOBAL_AUTH_DONT_HAVE_ACCOUNT', {
                  defaultValue: "Don't have an account?",
                })} `}
                <Link variant="subtitle2" component={RouterLink} to="/signup">
                  {t('GLOBAL_AUTH_SIGNUP', { defaultValue: 'Sign Up' })}
                </Link>
              </Typography>
            </>
          )}
        </ContentStyle>
      </Container>
    </Box>
  );
};

export default Login;
